/**
 * Change Plan Modal Component - Unified Admin Module
 * 
 * Reusable modal for changing user plans with custom limits support.
 * Used across admin dashboard and user detail views for consistency.
 */

import React, { useState, useEffect } from 'react';
import { useAdminData } from '../hooks/useAdminData';
import { getPlanInfo } from '../utils';
import type { PlanType, UserIdentifier } from '../types';
import { Button, Icon, Text, Select, Input, FormAlert } from '@ui-library';
import { IconName } from '@ui-library/Icon/Icon.type';

interface ChangePlanModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  userIdentifier: UserIdentifier;
  currentPlan?: PlanType;
  currentWeeklyAllowance?: number;
  currentMonthlyAllowance?: number;
  userDisplayName?: string;
}

/**
 * Unified change plan modal with custom limits support
 */
export const ChangePlanModal: React.FC<ChangePlanModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  userIdentifier,
  currentPlan = 'starter',
  currentWeeklyAllowance,
  currentMonthlyAllowance,
  userDisplayName
}) => {
  const { operations, billingConfig } = useAdminData();
  const [isClosing, setIsClosing] = useState(false);

  // Form state
  const [planForm, setPlanForm] = useState({
    planType: 'starter' as PlanType,
    monthlyLimit: ''
  });

  // Load billing configuration when modal opens
  useEffect(() => {
    if (isOpen) {
      billingConfig.loadConfig();
      setPlanForm({
        planType: 'starter' as PlanType,
        monthlyLimit: ''
      });
    }
  }, [isOpen]);

  // Update form when plan type changes to pre-populate current limits
  useEffect(() => {
    if (planForm.planType === 'monthly' && planForm.monthlyLimit === '' && billingConfig.config) {
      const monthlyValue = currentMonthlyAllowance !== undefined
        ? currentMonthlyAllowance.toString()
        : billingConfig.config.default_monthly_allowance.toString();

      setPlanForm(prev => ({
        ...prev,
        monthlyLimit: monthlyValue
      }));
    }
  }, [planForm.planType, planForm.monthlyLimit, currentMonthlyAllowance, billingConfig.config]);

  const handleChangePlan = async () => {
    if (!userIdentifier) return;

    let monthlyLimit: number | undefined;

    if (planForm.planType === 'monthly' && planForm.monthlyLimit) {
      monthlyLimit = parseFloat(planForm.monthlyLimit);
      if (isNaN(monthlyLimit) || monthlyLimit < 0) {
        alert('Please enter a valid monthly limit');
        return;
      }
    }

    const success = await operations.changePlan(
      userIdentifier,
      planForm.planType,
      undefined,
      monthlyLimit
    );

    if (success) {
      setPlanForm({ planType: 'starter' as PlanType, monthlyLimit: '' });
      onSuccess();
      handleClose();
    }
  };

  const handleClose = () => {
    setIsClosing(true);
    setTimeout(() => {
      setIsClosing(false);
      setPlanForm({ planType: 'starter' as PlanType, monthlyLimit: '' });
      operations.clearError();
      onClose();
    }, 300);
  };

  const planOptions = [
    { value: 'starter', label: 'Starter Plan' },
    { value: 'monthly', label: 'Monthly Plan' },
    { value: 'unlimited', label: 'Unlimited Plan' },
  ];

  const monthlyLimitHelperText = currentMonthlyAllowance !== undefined
    ? `Current: $${currentMonthlyAllowance}${billingConfig.config ? ` · Default: $${billingConfig.config.default_monthly_allowance}` : ''}`
    : billingConfig.config
      ? `Default: $${billingConfig.config.default_monthly_allowance}`
      : undefined;

  const title = `Change Plan${userDisplayName ? ` for ${userDisplayName}` : ''}`;

  if (!isOpen && !isClosing) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        className={`fixed inset-0 bg-alpha-black-70 z-[200] ${isClosing ? 'animate-fade-out' : 'animate-fade-in'}`}
        onClick={handleClose}
      />
      {/* Panel */}
      <div
        className={`fixed right-0 top-0 h-screen z-[200] bg-bg-primary w-[420px] flex flex-col border-l border-border-secondary-alt ${isClosing ? 'animate-slide-out-right' : 'animate-slide-in-right'}`}
        style={{
          boxShadow: '0 20px 24px -4px rgba(10, 13, 18, 0.08), 0 8px 8px -4px rgba(10, 13, 18, 0.03), 0 3px 3px -1.5px rgba(10, 13, 18, 0.04)',
        }}
      >
        {/* Header */}
        <div className="flex p-bg-v2-3xl items-center gap-bg-v2-md self-stretch justify-between">
          <Text variant="text-xl" weight="bold">{title}</Text>
          <button
            onClick={handleClose}
            className="flex items-center justify-center w-8 h-8 rounded-full text-fg-secondary hover:bg-bg-primary-hover hover:text-text-secondary-hover transition-all duration-200"
          >
            <Icon name={IconName.Cross} size="lg" inheritColor />
          </button>
        </div>

        {/* Divider */}
        <div className="box-border flex flex-col gap-[16px] items-center px-[var(--spacing-3xl,24px)] py-0 w-full">
          <div className="bg-border-secondary h-px w-full" />
        </div>

        {/* Content */}
        <div className="flex flex-col gap-bg-v2-3xl self-stretch px-bg-v2-3xl py-bg-v2-3xl overflow-y-auto flex-1">
          <Select
            label="New Plan Type"
            options={planOptions}
            value={planForm.planType}
            onChange={(val) => {
              const newPlanType = val as PlanType;
              if (newPlanType === 'monthly') {
                const monthlyValue = currentMonthlyAllowance !== undefined
                  ? currentMonthlyAllowance.toString()
                  : (billingConfig.config?.default_monthly_allowance?.toString() || '50.00');
                setPlanForm(prev => ({ ...prev, planType: newPlanType, monthlyLimit: monthlyValue }));
              } else {
                setPlanForm(prev => ({ ...prev, planType: newPlanType, monthlyLimit: '' }));
              }
            }}
          />

          {planForm.planType === 'monthly' && (
            <div className="bg-bg-secondary p-bg-v2-lg rounded-bg-v2-md space-y-bg-v2-md">
              <p className="text-sm text-text-secondary font-medium">
                Plan Limits (current values are pre-filled)
              </p>
              <Input
                label="Monthly Limit ($)"
                type="number"
                step="0.01"
                min="0"
                max="10000"
                value={planForm.monthlyLimit}
                onChange={(e) => setPlanForm(prev => ({ ...prev, monthlyLimit: e.target.value }))}
                placeholder={
                  currentMonthlyAllowance !== undefined
                    ? currentMonthlyAllowance.toString()
                    : (billingConfig.config?.default_monthly_allowance?.toString() ?? '50.00')
                }
                helperText={monthlyLimitHelperText}
              />
            </div>
          )}

          <div className="bg-bg-brand-primary p-bg-v2-lg rounded-bg-v2-md space-y-bg-v2-xs">
            <p className="text-sm text-text-brand-secondary">
              <strong>Current Plan:</strong> {getPlanInfo(currentPlan).label}
            </p>
            <p className="text-sm text-text-brand-secondary">
              <strong>New Plan:</strong> {getPlanInfo(planForm.planType).label}
            </p>
            {currentWeeklyAllowance && (
              <p className="text-sm text-text-brand-primary">
                <strong>Current Weekly Allowance:</strong> ${currentWeeklyAllowance}
              </p>
            )}
            {currentMonthlyAllowance && (
              <p className="text-sm text-text-brand-primary">
                <strong>Current Monthly Allowance:</strong> ${currentMonthlyAllowance}
              </p>
            )}
            <p className="text-sm text-text-brand-primary pt-bg-v2-xs">
              Changing the plan will update allowances but preserve existing credits.
            </p>
            {planForm.planType === 'monthly' && (
              <p className="text-sm text-text-brand-tertiary font-medium">
                💡 Current limit values are pre-filled - modify as needed or keep as-is.
              </p>
            )}
          </div>

          <div className="grid grid-cols-2 gap-bg-v2-xl text-sm">
            <div>
              <h5 className="font-medium text-text-secondary mb-bg-v2-md">Current Plan Features</h5>
              <div className="space-y-bg-v2-xs text-text-tertiary">
                <p>Plan: {getPlanInfo(currentPlan).label}</p>
                <p>Type: {getPlanInfo(currentPlan).description}</p>
                {currentPlan === 'unlimited' && (
                  <p className="text-fg-brand-primary font-medium">Unlimited Access</p>
                )}
              </div>
            </div>
            <div>
              <h5 className="font-medium text-text-secondary mb-bg-v2-md">New Plan Features</h5>
              <div className="space-y-bg-v2-xs text-text-tertiary">
                <p>Plan: {getPlanInfo(planForm.planType).label}</p>
                <p>Type: {getPlanInfo(planForm.planType).description}</p>
                {planForm.planType === 'unlimited' && (
                  <p className="text-fg-brand-primary font-medium">Unlimited Access</p>
                )}
              </div>
            </div>
          </div>

          <FormAlert message={operations.error ?? ''} variant="error" />
        </div>

        {/* Footer */}
        <div className="flex p-bg-v2-xl px-bg-v2-3xl justify-end items-center gap-bg-v2-xl self-stretch border-t border-border-secondary bg-bg-primary">
          <Button
            variant="outline"
            label="Cancel"
            onClick={handleClose}
            disabled={operations.loading}
          />
          <Button
            label={operations.loading ? 'Changing...' : planForm.planType === currentPlan ? 'Same Plan' : 'Change Plan'}
            onClick={handleChangePlan}
            disabled={operations.loading || planForm.planType === currentPlan}
            className="bg-utility-purple-600 hover:bg-utility-purple-700"
          />
        </div>
      </div>
    </>
  );
};

export default ChangePlanModal;
